import React from 'react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface CommonSelectOption {
    value: string;
    label: string;
    color?: string; // Optional CSS class for color dot (e.g. 'bg-green-500')
    icon?: React.ReactNode;
}

interface CommonSelectProps {
    value: string;
    onValueChange: (value: any) => void;
    options: CommonSelectOption[];
    placeholder?: string;
    disabled?: boolean;
    label?: string; // Optional side label
    className?: string;
    triggerClassName?: string;
    contentClassName?: string;
    error?: string;
}

export const CommonSelect: React.FC<CommonSelectProps> = ({
    value,
    onValueChange,
    options,
    placeholder = "Select an option",
    disabled = false,
    label,
    className,
    triggerClassName,
    contentClassName,
    error,
}) => {
    // console.log("options", placeholder);
    // Map empty string values to a special token ONLY if there's an actual empty option.
    // If there isn't one, passing undefined tells Radix to show the placeholder.
    const hasEmptyOption = options.some(opt => opt.value === "");
    const internalValue = (value === "" && !hasEmptyOption) ? undefined : (value === "" ? "__empty_value__" : value);

    const internalOptions = options.map((opt) => ({
        ...opt,
        value: opt.value === "" ? "__empty_value__" : opt.value,
    }));

    const handleValueChange = (val: string) => {
        onValueChange(val === "__empty_value__" ? "" : val);
    };

    return (
        <div className={cn("flex items-center gap-3", className)}>
            {label && (
                <p className="text-xs font-bold text-muted-foreground uppercase hidden sm:block whitespace-nowrap">
                    {label}
                </p>
            )}
            <Select
                key={value === "" ? "empty" : "filled"}
                value={internalValue}
                onValueChange={handleValueChange}
                disabled={disabled}
            >
                <SelectTrigger className={cn(
                    "w-[180px] h-10 rounded-xl font-bold border-border bg-background shadow-sm hover:border-primary/50 transition-all focus:ring-1 focus:ring-primary/20",
                    error && "border-red-500 focus:ring-red-500",
                    triggerClassName
                )}>
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent className={cn("rounded-xl border-border shadow-2xl p-1 overflow-hidden", contentClassName)}>
                    {internalOptions.map((option) => (
                        <SelectItem
                            key={option.value}
                            value={option.value}
                            className={cn(
                                "rounded-lg py-2.5 transition-colors cursor-pointer focus:bg-accent focus:text-accent-foreground"
                            )}
                        >
                            <div className="flex items-center gap-2">
                                {option.color && (
                                    <div className={cn("w-2 h-2 rounded-full", option.color)} />
                                )}
                                {option.icon && (
                                    <span className="opacity-70">{option.icon}</span>
                                )}
                                <span className="font-medium">{option.label}</span>
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
};
